import { DownOutlineIcon } from '@carboncopyco/icons';
import { TabType } from '@carboncopyco/types/desktop';
import { Dropdown, DropdownItem } from '@carboncopyco/ui';
import { useClickOutside } from '@carboncopyco/utils';
import { useEffect, useState } from 'react';
import { useFlagsFallback } from '../../hooks/useFlagsFallback';
import { useFocusAppService } from '../../hooks/useFocusAppService';
import { HOME_TAB_ID, useTabManager } from '../../services/TabManager';
import { useUser } from '../../store/context';

const WindowTitleBarDropdown = ({ iconColor }: { iconColor: string }) => {
  const { triggerFocus } = useFocusAppService();
  const flags = useFlagsFallback();
  const { getActiveGlobalTab } = useTabManager();
  const activeGlobalTab = getActiveGlobalTab();
  const { me } = useUser();
  const isLoginScreen = !me?.email;

  const [windowsDropdownMenuOpened, setWindowsDropdownMenuOpened] =
    useState(false);

  const [isUpdatesDisabled, setIsUpdatesDisabled] = useState<boolean>(true);

  const handleTitleBarClick = (titleBarFunc: () => void) => {
    titleBarFunc();
    setWindowsDropdownMenuOpened(false);
  };

  const openDevTools = () => {
    window.dsDesktop.view.onToggleAllDevTools();
    setWindowsDropdownMenuOpened(false);
  };

  const dropdownRef = useClickOutside(() => {
    setWindowsDropdownMenuOpened(false);
    if (
      activeGlobalTab?.type === TabType.BROWSER_WINDOW &&
      activeGlobalTab?.isBrowserUrlValid === false
    )
      return;
    triggerFocus(false);
  });

  const toggleDropdown = () => {
    if (windowsDropdownMenuOpened) {
      setWindowsDropdownMenuOpened(false);
      triggerFocus(false);
      return;
    }
    setWindowsDropdownMenuOpened(true);
    triggerFocus(true);
  };

  const handleIsUpdatesDisabled = () => {
    window.dsDesktop?.app
      .isUpdatesDisabled()
      .then((disabled) => {
        setIsUpdatesDisabled(!!disabled);
        return null;
      })
      .catch((e) => {
        setIsUpdatesDisabled(true);
        console.error('Error setting isUpdatesDisabled', e);
      });
  };

  useEffect(() => {
    handleIsUpdatesDisabled();
  }, []);

  return (
    <Dropdown
      open={windowsDropdownMenuOpened}
      align="end"
      triggerElement={
        <div
          className={`cursor-pointer text-${iconColor} items-center justify-center`}
          onClick={toggleDropdown}
        >
          <DownOutlineIcon />
        </div>
      }
    >
      <div ref={dropdownRef}>
        <DropdownItem
          label="About Demostack"
          onClick={() => handleTitleBarClick(window.dsDesktop?.app.showAboutUs)}
          key="aboutUs"
        />
        <DropdownItem
          disabled={isUpdatesDisabled}
          label="Check for updates"
          onClick={() =>
            handleTitleBarClick(window.dsDesktop?.app.checkForUpdates)
          }
          key="updates"
        />
        <DropdownItem
          disabled={isLoginScreen}
          label="App Settings"
          onClick={() =>
            handleTitleBarClick(window.dsDesktop?.appSettings?.openAppSettings)
          }
          key="appSettings"
        />
        {flags?.forceDevTools && (
          <DropdownItem
            label="Open Dev Tools"
            onClick={openDevTools}
            key="toggleAllDevTools"
          />
        )}
        <DropdownItem
          label="Send logs"
          onClick={() =>
            handleTitleBarClick(window.dsDesktop?.app.sendApplicationLogs)
          }
          key="appLogs"
        />
        <DropdownItem
          label="Diagnostic check"
          onClick={() =>
            handleTitleBarClick(
              window.dsDesktop?.diagnosticCheck?.openDiagnosticCheck
            )
          }
          key="diagnosticCheck"
        />
        <DropdownItem
          label="Show Logs"
          onClick={() => handleTitleBarClick(window.dsDesktop?.app.showLogs)}
          key="showLogs"
        />
        <DropdownItem
          label="Show Screen Recordings"
          onClick={() =>
            handleTitleBarClick(window.dsDesktop?.app.showScreenRecordings)
          }
          key="showScreenRecordings"
        />
        <DropdownItem
          label="Clear Cache and Restart"
          onClick={() =>
            handleTitleBarClick(window.dsDesktop?.app.clearCacheAndRestart)
          }
          key="clearCacheAndRestart"
        />
        <DropdownItem
          label="Open Help Center"
          onClick={() =>
            handleTitleBarClick(window.dsDesktop?.app.openHelpCenter)
          }
          key="openHelpCenter"
        />
        <DropdownItem
          label="Release Notes"
          onClick={() =>
            handleTitleBarClick(window.dsDesktop?.app.openReleaseNotes)
          }
          key="openReleaseNotes"
        />
        <DropdownItem
          label="Restart"
          onClick={() =>
            handleTitleBarClick(window.dsDesktop?.app.showRestartModal)
          }
          key="Restart"
        />
        <DropdownItem
          disabled={isLoginScreen}
          label="Go To (ctrl+g)"
          onClick={() =>
            handleTitleBarClick(window.dsDesktop?.goToAsset?.openGoToModal)
          }
          key="GoTo"
        />
        <DropdownItem
          disabled={isLoginScreen || activeGlobalTab.id === HOME_TAB_ID}
          label="Zoom in/out (ctrl+ / ctrl-)"
          onClick={() =>
            handleTitleBarClick(window.dsDesktop?.app.showZoomPopup)
          }
          key="ZoomInOut"
        />
      </div>
    </Dropdown>
  );
};

export default WindowTitleBarDropdown;
